/**
 * buildReportDocument — `ReportDocument` as a complete, self-contained HTML file.
 *
 * SELF-CONTAINED IS A CONTRACT, NOT AN ASPIRATION. No `<script>`, no external
 * stylesheet, no font URL, no image by URL.
 *
 * The reason is the reader: this file's whole job is to be forwarded. It gets
 * mailed to a client, opened on a plane, opened next year. A stylesheet fetched
 * from our CDN turns it into an unstyled wall of text the moment the wifi is off
 * or the URL moves, and it fails SILENTLY — the document still opens, it just
 * stops looking like the dashboard it claims to be.
 *
 * Nothing enforces this at the moment; it is held by this module and by
 * `buildReportDocument.test.tsx`, which asserts that every `src`/`href` AND
 * every CSS `url(` in the output is absent or a `data:` URI. The PDF work (branch
 * `feat/search-report-pdf-poc`) adds a server-side check that refuses a document
 * breaking the rule, which is a real second signature — worth restoring here if
 * that lands.
 *
 * THE STYLESHEET IS THE APP'S OWN, COMPILED. `renderToStaticMarkup` emits
 * Tailwind class names with nothing behind them — the file has no app CSS — so
 * the document must carry it. Inlining the built bundle rather than
 * hand-maintaining an export stylesheet is the same argument that moved this
 * export into the browser at all: a second, hand-kept copy of the styling would
 * drift from the first. Measured: 40 KB, carrying every `--chart-*` token and
 * every `.stroke-chart-*` rule.
 *
 * WHICH IS WHY THE APP'S WEBFONT STAYS OUT OF THAT STYLESHEET. Inter is
 * registered by `@nu-assist/ui/fonts.css`, imported from `main.tsx` and
 * deliberately NOT from `index.css`: an `@font-face { src: url(...) }` compiled
 * into the string below is exactly the dead-offline URL this contract forbids,
 * and it would fail the quiet way, by falling back. So this document renders in
 * the reader's system font, as it always has. That is the intended outcome, not
 * a gap; embedding the face as `data:` would be the way to change it.
 *
 * `data-theme="light"` IS LOAD-BEARING. `:root` in that bundle is the DARK theme
 * (`color-scheme: dark; --bg: #0a0a0a`); light lives under `[data-theme=light]`.
 * A document that inherited the default would download as a black page — fine to
 * miss on screen, obvious and expensive the first time somebody prints one.
 *
 * `data-type-scale="root"` IS THE OTHER HALF. Light carries a type scale one
 * pixel larger than `:root`, accepted for the app on the grounds that toggling
 * the theme "re-wraps text slightly". This document has no toggle: it is always
 * light, it is laid out at a fixed width against `@page A4`, and its sections
 * refuse to break across pages. A pixel on every caption makes every table row
 * taller and moves the page breaks of a file a client prints, and no suite
 * measures pagination, so it would move them silently. PAGINATION IS PART OF
 * THE CONTRACT: the attribute opts out of the larger sizes
 * (`[data-theme="light"]:not([data-type-scale="root"])` in `tokens.css`) and
 * keeps every light colour.
 */

import { renderToStaticMarkup } from "react-dom/server";

import { DOCUMENT_WIDTH, ReportDocument, type ReportDocumentProps } from "./ReportDocument";

/**
 * Print rules, and the layout the app's own stylesheet has no opinion about.
 *
 * `print-color-adjust: exact` is the one that matters. Chrome prints with
 * "Background graphics" OFF by default, which strips every panel fill, every
 * chart band and every table stripe — a page that is correct on screen prints as
 * white boxes with floating text. This asks for the colours back; a reader who
 * genuinely wants ink-free output can still turn them off in the dialog.
 */
const DOCUMENT_CSS = `
  html, body { background: var(--bg); }
  body { margin: 0; font-family: var(--font-sans); }
  [data-testid="report-document"] { max-width: ${DOCUMENT_WIDTH}px; }
  @page { size: A4 portrait; margin: 12mm; }
  @media print {
    html, body, [data-testid="report-document"] {
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    /* A panel split across a page break loses the header that says what its
       numbers are. Tables are allowed to break — a 1300-row table has to. */
    section { break-inside: avoid; }
    table { break-inside: auto; }
    thead { display: table-header-group; }
    tr { break-inside: avoid; }
  }
`;

export interface BuildReportDocumentOptions extends ReportDocumentProps {
  /**
   * The app's compiled CSS.
   *
   * Injected rather than imported here so this module stays pure and testable:
   * a test passes a stub instead of running Tailwind. `loadDocumentCss()` is the
   * production source.
   */
  css: string;
  /** Filename-less document title; becomes `<title>`. */
  title: string;
}

export function buildReportDocument({
  css,
  title,
  ...documentProps
}: BuildReportDocumentOptions): string {
  const body = renderToStaticMarkup(<ReportDocument {...documentProps} />);
  return `<!doctype html>
<html lang="en" data-theme="light" data-type-scale="root">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<title>${escapeHtml(title)}</title>
<style>${css}</style>
<style>${DOCUMENT_CSS}</style>
</head>
<body>${body}</body>
</html>`;
}

/**
 * The app's compiled CSS, fetched on demand.
 *
 * A dynamic import so the 40 KB rides in its own chunk and costs nothing to
 * everyone who never exports. `?inline` is Vite's "give me the processed CSS as
 * a string" — the same bytes the app's own stylesheet is built from, Tailwind
 * having already dropped every unused utility.
 */
export async function loadDocumentCss(): Promise<string> {
  const module = await import("../../../../index.css?inline");
  return module.default;
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}
